/**
 * KeyPairsEditor - reusable N-key-pair editor for join contracts.
 *
 * Value: `[{ left_field, right_field }, ...]`
 * onChange: `(newKeys) => void`
 */
import React from "react";
import { Plus, Trash2, X } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export function KeyPairsEditor({
  value = [{ left_field: "", right_field: "" }],
  onChange,
  leftColumns = [],
  rightColumns = [],
  leftLabel = "Left.field",
  rightLabel = "Right.field",
  testIdPrefix = "keypair",
}) {
  const upd = (i, patch) => onChange(value.map((k, idx) => (idx === i ? { ...k, ...patch } : k)));
  const add = () => onChange([...value, { left_field: "", right_field: "" }]);
  const rm = (i) => onChange(value.filter((_, idx) => idx !== i));

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <Label className="text-[10px] text-[color:var(--ink-3)]">Key pairs (all must match — ANDed)</Label>
        <Button size="sm" variant="ghost" onClick={add} data-testid={`${testIdPrefix}-add`} className="h-6 text-[color:var(--brand-ocean)]">
          <Plus className="w-3 h-3 mr-1" />Add key
        </Button>
      </div>
      <div className="space-y-1.5">
        {value.map((k, i) => (
          <div key={i} className="flex gap-1.5 items-center">
            <Select value={k.left_field} onValueChange={(v) => upd(i, { left_field: v })}>
              <SelectTrigger data-testid={`${testIdPrefix}-left-field-${i}`} className="text-xs"><SelectValue placeholder={leftLabel} /></SelectTrigger>
              <SelectContent>{leftColumns.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
            <span className="text-[color:var(--ink-3)] text-[10px]">=</span>
            <Select value={k.right_field} onValueChange={(v) => upd(i, { right_field: v })}>
              <SelectTrigger data-testid={`${testIdPrefix}-right-field-${i}`} className="text-xs"><SelectValue placeholder={rightLabel} /></SelectTrigger>
              <SelectContent>{rightColumns.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
            {value.length > 1 && (
              <Button size="icon" variant="ghost" onClick={() => rm(i)} className="h-7 w-7 text-[color:var(--danger)]">
                {testIdPrefix.startsWith("joins") ? <Trash2 className="w-3.5 h-3.5" /> : <X className="w-3.5 h-3.5" />}
              </Button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
